import { useState } from "react";
import { dispatch } from "../../state/store";
import {
  pushCreatedTableRowsIds,
  setFwuCampaingsRefresh,
  setNotificationCampaignsRefresh
} from "../../state/ducks";

import { fwuCampaignsErrors, strCode } from "../../consts";
import { fwuCampaignsService, log, notificationService, t } from "../../services";
import { csvUtil, responseUtil } from "../../utils";
import { useCreateNotificationCampaign } from "../notificationCampaigns/useCreateNotificationCampaign";

import { Paragraph2, TextButton } from "../../components";

export const parseErrorMessage = (err) => {
  const error = err?.response?.data?.error;

  let errorMessage = fwuCampaignsErrors[error] ?? error ?? err?.toString();

  if (error?.includes("INVALID_PARAMETER_PROVIDED")) {
    const invalidParameter = error.split(":")[1];
    errorMessage = t(strCode.INVALID_PARAMETER_PROVIDED, { invalidParameter });
  }

  if (error?.includes("INVALID_DUSNS_AND_USER_IDS_PROVIDED")) {
    const info = error.split(":")[1];
    errorMessage = t(strCode.INVALID_DUSNS_AND_USER_IDS_PROVIDED, { info });
  }

  if (error?.includes("INVALID_DETAILS_PROVIDED")) {
    const details = error.split(":")[1];
    errorMessage = t(strCode.INVALID_DETAILS_PROVIDED, { details });
  }

  return errorMessage;
};

const onDownloadCsv = (dusns) => {
  const formattedDusns = dusns.map((dusn) => {
    return { dusn };
  });

  const incorrectDusnsFile = csvUtil.createCSVFile(formattedDusns);
  csvUtil.downloadCSVFile(incorrectDusnsFile, "incorrectDUSNs");
};

const handleIncorrectDusns = (res, campaignName) => {
  const incorrectDusns = res?.data?.data?.incorrect_dusns;

  if (incorrectDusns && incorrectDusns?.length) {
    const createCampaignMessage = t(strCode.CREATE_CAMPAIGN_SUCCESS_INFO, {
      name: campaignName
    });

    const dusnsMessage =
      incorrectDusns.length > 1
        ? strCode.INCOMPATIBLE_DEVICES_INFO
        : strCode.INCOMPATIBLE_DEVICE_INFO;

    notificationService.showSuccess({
      title: t(strCode.CREATE_CAMPAIGN_SUCCESS),
      Component: (
        <Paragraph2>
          {createCampaignMessage}
          <p>
            {t(dusnsMessage, {
              count: incorrectDusns.length
            })}
          </p>

          <TextButton
            style={{ height: "24px", textDecoration: "underline" }}
            onClick={() => onDownloadCsv(incorrectDusns)}>
            {t(strCode.DONWLOAD_LIST_BTN)}
          </TextButton>
        </Paragraph2>
      )
    });
  }

  return incorrectDusns;
};

export const useCreateFWUCampaign = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const { mutationCallback: createNotificationCampaign } = useCreateNotificationCampaign();

  const onSuccess = (fwuCampaign) => {
    notificationService.showSuccess({
      title: t(strCode.CREATE_CAMPAIGN_SUCCESS),
      message: t(strCode.CREATE_CAMPAIGN_SUCCESS_INFO, {
        name: fwuCampaign.campaign_name
      })
    });
  };

  const mutationCallback = async (fwuCampaign, fwNotificationCampaign) => {
    setLoading(true);
    setError(false);

    try {
      let notificationCampaignRes;

      if (fwNotificationCampaign) {
        notificationCampaignRes = await createNotificationCampaign(fwNotificationCampaign);
      }

      const fwuCampaignRes = await fwuCampaignsService.createFWUCampaign(fwuCampaign);

      if (
        (notificationCampaignRes &&
          responseUtil.isResponseSuccessful(fwuCampaignRes) &&
          responseUtil.isResponseSuccessful(notificationCampaignRes)) ||
        (!notificationCampaignRes && responseUtil.isResponseSuccessful(fwuCampaignRes))
      ) {
        dispatch(pushCreatedTableRowsIds([fwuCampaign.campaign_name]));
        const incorrectDusns = handleIncorrectDusns(fwuCampaignRes, fwuCampaign.campaign_name);

        // After we receive response from the backend that request was completed, around 1 second passes
        // before newly created record will actually be added to the fwuCampaign list
        if (!incorrectDusns) {
          onSuccess(fwuCampaign);
        }

        setTimeout(() => {
          setLoading(false);
          dispatch(setFwuCampaingsRefresh(true));
          dispatch(setNotificationCampaignsRefresh(true));
        }, 1500);
      }
    } catch (err) {
      setError(true);
      setLoading(false);

      log.error("useCreateFWUCampaign: Failed to create FWU campaign", err);

      notificationService.showError({
        title: t(strCode.CREATE_FWU_CAMPAIGN_ERROR),
        message: t(parseErrorMessage(err))
      });
    }
  };

  return { mutationCallback, loading, error, handleIncorrectDusns };
};
